// deviceIdentity — стабильный идентификатор УСТРОЙСТВА ВХОДА (не человека).
//
// Хранится в localStorage (PWA/браузер/WebView2) и уходит в Login/Register:
// сервер привязывает его к сессии (spisok «Мои устройства»), а после
// подтверждения телефона — помечает сессию доверенной. Никаких сетевых
// отпечатков и скрытых идентификаторов: только локальный UUID + понятное имя.
import { isWailsMode } from '@/lib/wailsBridge';

const DEVICE_ID_KEY = 'hydra_device_id';

/** UUID v4 (crypto.randomUUID, иначе getRandomValues); '' — среда без crypto. */
function randomDeviceUUID(): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (!cryptoApi) return '';
  if (typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
  if (typeof cryptoApi.getRandomValues !== 'function') return '';
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** DeviceID текущего устройства (создаётся один раз, переживает перезагрузку). */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing;
    const created = randomDeviceUUID();
    if (created) localStorage.setItem(DEVICE_ID_KEY, created);
    return created;
  } catch {
    // Приватный режим/запрещённое хранилище: вход не блокируем — устройство
    // просто не будет привязано (сессия не доверенная).
    return '';
  }
}

/** Тип устройства для имени (desktop/mobile/web). */
export function deviceKind(): string {
  if (isWailsMode()) return 'desktop';
  if (typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return 'mobile';
  return 'web';
}

function deviceOS(): string {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Mac OS X/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return '';
}

const KIND_LABEL: Record<string, string> = {
  desktop: 'Компьютер',
  mobile: 'Телефон',
  web: 'Браузер',
};

/** Понятное имя устройства по умолчанию («Браузер (Windows)»). */
export function deviceDisplayName(): string {
  const kind = deviceKind();
  const label = KIND_LABEL[kind] || 'Устройство';
  const os = deviceOS();
  return os ? `${label} (${os})` : label;
}

/** Данные устройства для Login/Register. */
export function deviceLoginInfo(): { deviceId: string; deviceName: string } {
  return { deviceId: getOrCreateDeviceId(), deviceName: deviceDisplayName() };
}
